/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { usePreviewImage } from '@euler/app/components/media/ImageViewerContext';
import { useTakePhoto } from '@euler/app/flows/media/functions/useTakePhoto';
import { useUserProfileManager } from '@euler/app/flows/settings/functions';
import { useGenderPicker } from '@euler/app/flows/settings/functions/useGenderPicker';
import { useJobTitlePicker } from '@euler/app/flows/settings/functions/useJobTitlePicker';
import { maskedMobilePhone } from '@euler/app/tv/functions/maskedMobilePhone';
import { Avatar } from '@euler/components/Avatar';
import { Icon } from '@euler/components/Icomoon';
import { TableView } from '@euler/components/TableView';
import { FontFamily } from '@euler/components/typography';
import { roleStringToUserRoles } from '@euler/functions/orgUserUtils';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { useCurrentUserRole } from '@euler/functions/user/useCurrentUserRole';
import { roleNameFromRoleType } from '@euler/functions/user/util';
import { usePrompt } from '@euler/functions/ux/usePrompt';
import {
  useScreenContentContainerStyle,
  wrapNavigatorScreen,
} from '@euler/functions/wrapNavigatorScreen';
import { Gender } from '@euler/model/enum';
import { showAlert } from '@euler/utils';
import { formatDate } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import { isEmail } from '@euler/utils/validators';
import { DrawerActions, useNavigation } from '@react-navigation/native';
import { memo, useCallback } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  SharedValue,
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';
import { NotSetLabel } from '../components/NotSetLabel';
import { Cell2 as Cell, Section } from '../components/SettingsTable';

const kAvatarSize = 120;
const kAvatarBorder = 10;

export const UserProfileScreen = wrapNavigatorScreen(
  () => {
    const { rpx } = useResponsiveContext();
    const prompt = usePrompt();
    const contentContainerStyle = useScreenContentContainerStyle();
    const {
      profile,
      updateAsync,
      uploadAndSetAvatarAsync,
      isUploadingAvatar,
      uploadProgress,
    } = useUserProfileManager();

    const role = useCurrentUserRole();

    const previewAvatar = usePreviewImage();

    const onAvatarPress = usePersistFn(() => {
      if (profile.user.avatar) {
        previewAvatar({ imageUrls: [profile.user.avatar] });
      }
    });

    const takePhoto = useTakePhoto({
      mask: { aspectRatio: 1 },
      initialPosition: 'front',
    });

    const onEditAvatar = useCallback(() => {
      takePhoto(async result => {
        await uploadAndSetAvatarAsync(result.uri);
      });
    }, [takePhoto, uploadAndSetAvatarAsync]);

    const onNamePress = useCallback(async () => {
      const name = (
        await prompt({
          title: '修改用户名称',
          defaultValue: profile.user.name,
        })
      )?.trim();
      if (name) {
        await updateAsync({ name });
      }
    }, [profile.user.name, prompt, updateAsync]);

    const onNickPress = useCallback(async () => {
      const nick = (
        await prompt({
          title: '修改昵称',
          defaultValue: profile.user.nick,
        })
      )?.trim();
      if (nick) {
        await updateAsync({ nick });
      }
    }, [profile.user.nick, prompt, updateAsync]);

    const onGenderPress = useGenderPicker(async (gender: Gender) => {
      await updateAsync({ gender });
    });

    const onEmailPress = useCallback(async () => {
      const email = (
        await prompt({
          title: '设置您的工作Email',
          defaultValue: profile.user.email,
        })
      )?.trim();
      if (email) {
        if (!isEmail(email)) {
          void showAlert('Email格式不正确');
          return;
        }
        await updateAsync({ email });
      }
    }, [profile.user.email, prompt, updateAsync]);

    const onJobTitlePress = useJobTitlePicker(async title => {
      await updateAsync({ title });
    });

    return (
      <ScrollView
        contentContainerStyle={[
          {
            paddingTop: rpx(15),
            paddingBottom: rpx(15),
          },
          contentContainerStyle,
        ]}
      >
        <AvatarView
          avatar={profile.user.avatar}
          name={profile.user.name ?? profile.user.nick ?? profile.user.userName}
          isUploading={isUploadingAvatar}
          uploadProgress={uploadProgress}
          onPress={onAvatarPress}
          onEdit={onEditAvatar}
          css={`
            margin-top: ${rpx(15)}px;
            margin-bottom: ${rpx(15)}px;
          `}
        />
        <TableView>
          <Section title="基本信息">
            <Cell
              title="用户名"
              detail={profile.user.userName}
              detailTextStyle={{ marginRight: rpx(10) }}
            />
            <Cell
              title="用户名称"
              detail={profile.user.name || <NotSetLabel />}
              detailIcon={'disclosure'}
              onPress={onNamePress}
            />
            <Cell
              title="工作职位"
              detail={profile.user.jobTitle || <NotSetLabel />}
              detailIcon={'disclosure'}
              onPress={onJobTitlePress}
            />
          </Section>

          <Section title="其他信息">
            <Cell
              title="昵称"
              detail={profile.user.nick || <NotSetLabel />}
              detailIcon={'disclosure'}
              onPress={onNickPress}
            />
            <Cell
              title="性别"
              detail={
                profile.user.gender === Gender.Male ? (
                  '男'
                ) : profile.user.gender === Gender.Female ? (
                  '女'
                ) : (
                  <NotSetLabel />
                )
              }
              detailIcon={'disclosure'}
              onPress={onGenderPress}
            />
            <Cell
              title="Email"
              detail={profile.user.email || <NotSetLabel />}
              detailIcon={'disclosure'}
              onPress={onEmailPress}
            />
            <Cell
              title="手机号"
              detail={maskedMobilePhone(profile.user.mobile) || '(未设置)'}
              detailTextStyle={{ marginRight: rpx(10) }}
            />
            <Cell
              title="用户角色"
              detailTextStyle={{
                fontSize: rpx(14),
                marginRight: rpx(10),
                letterSpacing: rpx(-1),
              }}
              detail={roleStringToUserRoles(role)
                .map(roleNameFromRoleType)
                .join('/')}
            />
            <Cell
              title="注册时间"
              detail={
                profile.user.createdAt
                  ? formatDate(profile.user.createdAt)
                  : '-'
              }
              detailTextStyle={{ marginRight: rpx(10) }}
            />
          </Section>
        </TableView>
      </ScrollView>
    );
  },
  {
    title: '个人资料',
    headerLeft: props => <UserProfileHeaderLeft canGoBack={props.canGoBack} />,
  },
);

// At the Me-tab root the profile screen has no back button, so the left
// slot hosts a burger that opens the app drawer (mirroring the dashboard
// header). When the same screen is pushed inside the Settings flow it can
// go back, so it shows the back button instead. On desktop the sidebar
// replaces the drawer, so the root shows nothing.
const UserProfileHeaderLeft = memo(({ canGoBack }: { canGoBack?: boolean }) => {
  const { rpx } = useResponsiveContext();
  const isDesktop = useIsDesktopLayout();
  const navigation = useNavigation();
  const tint = isDesktop ? '#0f172a' : '#fff';

  const onBack = useCallback(() => navigation.goBack(), [navigation]);
  const onOpenDrawer = useCallback(
    () => navigation.dispatch(DrawerActions.openDrawer()),
    [navigation],
  );

  if (canGoBack) {
    return (
      <TouchableOpacity
        onPress={onBack}
        hitSlop={PredefinedHitSlops.large}
        style={{ paddingLeft: rpx(6), paddingRight: rpx(12) }}
      >
        <BackIcon fill={tint} />
      </TouchableOpacity>
    );
  }
  if (isDesktop) return null;
  return (
    <TouchableOpacity
      onPress={onOpenDrawer}
      hitSlop={PredefinedHitSlops.large}
      style={{ paddingLeft: rpx(16), paddingRight: rpx(12) }}
    >
      <Icon name="bars" color={tint} size={rpx(20)} />
    </TouchableOpacity>
  );
});

const AvatarView = memo(
  ({
    avatar,
    name,
    isUploading,
    uploadProgress,
    style,
    onPress,
    onEdit,
  }: {
    avatar?: string;
    name: string;
    isUploading: boolean;
    uploadProgress: SharedValue<number>;
    style?: StyleProp<ViewStyle>;
    onPress?: () => void;
    onEdit?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const avatarSize = rpx(kAvatarSize);
    const avatarBorder = rpx(kAvatarBorder);

    const progressStyle = useAnimatedStyle(() => ({
      transform: [
        {
          translateY: interpolate(
            uploadProgress.value,
            [0, 1],
            [avatarSize, 0],
          ),
        },
      ],
    }));
    return (
      <TouchableOpacity
        onPress={onPress}
        css={`
          width: ${avatarSize + 2 * avatarBorder}px;
          height: ${avatarSize + 2 * avatarBorder}px;
          border-radius: ${(avatarSize + 2 * avatarBorder) / 2}px;
          background-color: rgba(0, 0, 0, 0.05);
          align-items: center;
          justify-content: center;
          align-self: center;
        `}
        style={style}
      >
        <Avatar
          uri={avatar}
          name={name}
          textStyle={{
            fontFamily: FontFamily.NotoSans.Bold,
            fontSize: rpx(44),
            lineHeight: rpx(64),
          }}
          css={`
            width: ${avatarSize}px;
            height: ${avatarSize}px;
            border-radius: ${avatarSize / 2}px;
          `}
        />
        {isUploading && (
          <View
            css={`
              position: absolute;
              left: ${avatarBorder}px;
              top: ${avatarBorder}px;
              width: ${avatarSize}px;
              height: ${avatarSize}px;
              border-radius: ${avatarSize / 2}px;
              overflow: hidden;
            `}
          >
            <Animated.View
              css={`
                flex: 1;
                background-color: rgba(0, 0, 0, 0.25);
              `}
              style={progressStyle}
            />
          </View>
        )}
        <TouchableOpacity
          onPress={isUploading ? undefined : onEdit}
          css={`
            position: absolute;
            right: ${rpx(6)}px;
            top: ${rpx(6)}px;
            width: ${rpx(40)}px;
            height: ${rpx(40)}px;
            padding: ${rpx(4)}px;
            background-color: #fff;
            border-radius: ${rpx(20)}px;
            overflow: hidden;
          `}
        >
          <View
            css={`
              flex: 1;
              background-color: rgba(0, 0, 0, 0.05);
              width: 100%;
              border-radius: ${rpx(16)}px;
              align-items: center;
              justify-content: center;
            `}
          >
            {isUploading ? (
              <ActivityIndicator
                size={'small'}
                color="#333"
                css={`
                  transform: scale(${rpx(0.75)});
                `}
              />
            ) : (
              <Icon name="pencil" size={rpx(16)} color={'#5ba0e0'} />
            )}
          </View>
        </TouchableOpacity>
      </TouchableOpacity>
    );
  },
);
